import { useAlert } from 'react-alert';
import { Box, Typography, alpha } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { AddPhotoAlternateOutlined } from '@mui/icons-material';
import { ACCEPTABLE_IMAGE_EXTENSIONS, MAX_IMAGE_UPLOAD_SIZE } from '@uplift/constants';
import { SimpleDropzone } from './SimpleDropzone.component';

export type PhotoDropzoneProps = {
  image: string | undefined;
  onDropAccepted: (file: File) => void;
};

export const PhotoDropzone: React.VFC<PhotoDropzoneProps> = props => {
  const { classes, cx } = usePhotoDropzoneStyles();
  const alert = useAlert();

  return (
    <SimpleDropzone
      maxSize={MAX_IMAGE_UPLOAD_SIZE}
      accept={{ 'image/*': ACCEPTABLE_IMAGE_EXTENSIONS }}
      activeOnHover={!!props.image}
      onDropAccepted={(files: File[]) => props.onDropAccepted(files[0])}
      onDropRejected={rejected => {
        const message = rejected?.[0]?.errors?.[0].message;

        if (message) {
          alert.error(message);
        }
      }}
    >
      {state => {

        if (props.image) {

          return (
            <Box className={classes.imagePreview}>
              <Box display="flex" height="95%" width="auto">
                <img src={props.image} alt="Uploaded headshot" height="100%" width="100%" />
              </Box>
              <Box className={cx(classes.imagePreviewOverlay, classes.textContainer, {
                [classes.opacity1]: state.isDragActive,
              })}
              >
                <AddPhotoAlternateOutlined className={classes.icon} />
                <DropzoneMessage
                  isActive={state.isDragActive}
                  isReject={state.isDragReject}
                  hasFile
                />
              </Box>
            </Box>
          );
        }


        return (
          <Box className={classes.textContainer}>
            <AddPhotoAlternateOutlined className={classes.icon} />
            <DropzoneMessage
              isActive={state.isDragActive}
              isReject={state.isDragReject}
            />
            <Typography variant="body2">Use a photo (JPEG, PNG) that is at least 250 pixels and has a file size that is less than 2MB.</Typography>
          </Box>
        );
      }}
    </SimpleDropzone>
  );
};

type DropzoneMessageProps = {
  isActive: boolean;
  isReject: boolean;
  hasFile?: boolean;
};

const DropzoneMessage: React.VFC<DropzoneMessageProps> = ({ isActive, isReject, hasFile }) => {
  const { classes } = usePhotoDropzoneStyles();

  if (isActive) {
    if (isReject) {
      return (
        <Typography color="textPrimary">
          <span style={{ fontWeight: 600 }}>File format is not allowed</span>
        </Typography>
      );
    }

    return (
      <Typography color="textPrimary">
        <span style={{ fontWeight: 600 }}>Drop file here</span>
      </Typography>
    );
  }

  if (hasFile) {
    return (
      <Typography color="textPrimary">
        <span style={{ fontWeight: 600 }}>Upload a different photo</span>
      </Typography>
    );
  }

  return (
    <Typography color="textPrimary">
      <span style={{ fontWeight: 600 }}>Drag & drop here, or&nbsp;</span><Typography component="span" color="secondary" className={classes.browseButton}>Browse</Typography>
    </Typography>
  );
};

const usePhotoDropzoneStyles = makeStyles({ name: 'PhotoDropzone' })(theme => ({
  imagePreview: {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  imagePreviewOverlay: {
    position: 'absolute',
    inset: '2px',
    backgroundColor: alpha(theme.palette.secondary.main, 0.2),
    backdropFilter: 'blur(1px)',
    opacity: 0,
    '&:hover': {
      opacity: 1
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      backgroundColor: alpha(theme.palette.common.white, 0.7),
      zIndex: -1,
    }
  },
  opacity1: {
    opacity: 1,
  },
  textContainer: {
    userSelect: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    textAlign: 'center',
  },
  icon: {
    fontSize: '48px',
    color: theme.palette.grey[700]
  },
  browseButton: {
    position: 'relative',
    cursor: 'pointer',

    '&:after': {
      content: '""',
      backgroundColor: 'currentColor',
      position: 'absolute',
      width: '100%',
      height: '1px',
      left: 0,
      bottom: '2px',
    }
  }
}));
